import { Injectable, inject, signal, computed } from '@angular/core';
import { MessageService } from 'primeng/api';
import { StoryService } from './story.service';
import { STORY_DEMO_ID_STORAGE_KEY } from './story-demo.constants';
import type { DemoListItem } from './story-demo.types';

export type DemoMode = 'default-template' | 'story-demo';

@Injectable({ providedIn: 'root' })
export class StoryDemoFacade {
  private static readonly STEP_SYNC_DEBOUNCE_MS = 180;

  private readonly story = inject(StoryService);
  private readonly messageService = inject(MessageService);

  // ---------------------------------------------------------------------------
  // Private state signals
  // ---------------------------------------------------------------------------

  private readonly _currentStep = signal<number>(1);
  private readonly _confirmedStep = signal<number>(1);
  private readonly _targetStep = signal<number>(1);
  private readonly _maxStep = signal<number>(0);
  private readonly _demoId = signal<string>('');
  private readonly _isLoading = signal<boolean>(false);
  private readonly _isAvailable = signal<boolean>(false);
  private readonly _currentPayload = signal<Record<string, unknown>>({});
  private readonly _mode = signal<DemoMode>('default-template');
  private readonly _availableDemos = signal<DemoListItem[]>([]);
  private readonly _recommendedStartStep = signal<number>(1);
  private _syncInFlight = false;
  private _forceRefresh = false;
  private _stepSyncTimer: ReturnType<typeof setTimeout> | null = null;

  // ---------------------------------------------------------------------------
  // Public readonly signals
  // ---------------------------------------------------------------------------

  readonly currentStep = this._currentStep.asReadonly();
  readonly confirmedStep = this._confirmedStep.asReadonly();
  readonly maxStep = this._maxStep.asReadonly();
  readonly demoId = this._demoId.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly isAvailable = this._isAvailable.asReadonly();
  readonly currentPayload = this._currentPayload.asReadonly();
  readonly mode = this._mode.asReadonly();
  readonly availableDemos = this._availableDemos.asReadonly();
  readonly recommendedStartStep = this._recommendedStartStep.asReadonly();

  // ---------------------------------------------------------------------------
  // Computed
  // ---------------------------------------------------------------------------

  readonly stepLabel = computed(() => {
    const cur = this._currentStep();
    const max = this._maxStep();
    if (max > 0) return `Step ${cur} / ${max}`;
    return `Step ${cur}`;
  });

  /** Reset button label — reflects recommendedStartStep when it differs from 1. */
  readonly resetLabel = computed(() => {
    const s = this._recommendedStartStep();
    return s === 1
      ? 'Reset to step 1'
      : `Reset to recommended step (Step ${s})`;
  });

  /**
   * True only when a story-demo is loaded and the service is available.
   * Step controls should be disabled when this is false.
   */
  readonly canStep = computed(
    () => this._mode() === 'story-demo' && this._isAvailable()
  );
  readonly isStepSyncPending = computed(
    () => this._mode() === 'story-demo' && this._currentStep() !== this._confirmedStep()
  );

  // ---------------------------------------------------------------------------
  // Constructor — load saved ID then probe /demos
  // ---------------------------------------------------------------------------

  constructor() {
    // Runtime availability should reflect successful connectivity checks,
    // not only static environment configuration.
    this._isAvailable.set(false);
    this.loadDemoIdFromStorage();
    // Non-blocking: probe /demos, validate saved ID, populate dropdown.
    // Default template is already active — UI is never blank.
    if (this.story.isAvailable()) {
      this.initializeFromDemos();
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /** Set demo ID and persist to localStorage. Does NOT trigger a load — use applyDemoId for that. */
  setDemoId(id: string): void {
    this._demoId.set(id);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORY_DEMO_ID_STORAGE_KEY, id);
    }
  }

  /**
   * Apply demo ID from UI (dropdown selection or programmatic).
   * Trims input; if empty clears demo and returns to default-template;
   * otherwise persists and loads the demo.
   */
  applyDemoId(id: string): void {
    const trimmed = (id ?? '').trim();
    if (!trimmed) {
      this.clearStepSyncTimer();
      this._demoId.set('');
      this._mode.set('default-template');
      this._currentPayload.set({});
      this._currentStep.set(1);
      this._confirmedStep.set(1);
      this._targetStep.set(1);
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(STORY_DEMO_ID_STORAGE_KEY);
      }
      this.showToast('info', 'Demo ID cleared. Using Default Template.');
      return;
    }
    // Clear stale payload and step from the previous demo before the async
    // load begins, so any reactive effects see a clean slate immediately
    // rather than briefly showing the old demo's content.
    this.clearStepSyncTimer();
    this._currentPayload.set({});
    this._currentStep.set(1);
    this._confirmedStep.set(1);
    this._targetStep.set(1);
    this.setDemoId(trimmed);
    this.loadDemoAndEnterStoryMode(trimmed);
  }

  stepBack(): void {
    if (this._mode() !== 'story-demo') return;
    const id = this._demoId();
    if (!id || !this._isAvailable()) return;
    const cur = this._currentStep();
    if (cur <= 1) return;
    this.enqueueTargetStep(cur - 1);
  }

  stepForward(): void {
    if (this._mode() !== 'story-demo') return;
    const id = this._demoId();
    if (!id || !this._isAvailable()) return;
    const cur = this._currentStep();
    const max = this._maxStep();
    if (max > 0 && cur >= max) return;
    this.enqueueTargetStep(max > 0 ? Math.min(max, cur + 1) : cur + 1);
  }

  reset(): void {
    if (this._mode() !== 'story-demo') return;
    const id = this._demoId();
    if (!id || !this._isAvailable()) return;
    const target = Math.max(1, this._recommendedStartStep());
    this._currentPayload.set({});
    this._forceRefresh = true;
    this.enqueueTargetStep(target);
  }

  jumpTo(step: number): void {
    const id = this._demoId();
    if (!id || !this._isAvailable()) return;
    const max = this._maxStep();
    if (step < 1 || (max > 0 && step > max)) {
      this.showToast(
        'warn',
        `Please enter a valid step (1${max > 0 ? ` to ${max}` : ''}).`
      );
      return;
    }
    if (step === this._currentStep()) return;
    this._currentPayload.set({});
    this.enqueueTargetStep(step);
  }

  /**
   * Load meta + reset + getCurrent for the given demo ID.
   * Used when a demo ID is already known to be valid (e.g. from refreshState).
   */
  refreshState(): void {
    const id = this._demoId();
    if (!id || !this.story.isAvailable()) return;
    this._isLoading.set(true);
    this.story
      .getMeta(id)
      .then((meta) => {
        this._maxStep.set(meta.maxStep);
        this._recommendedStartStep.set(meta.recommendedStartStep);
        this._isAvailable.set(true);
        return this.story.reset(id);
      })
      .then(() => this.story.getCurrent(id))
      .then((current) => {
        this._currentStep.set(current.pointer.step);
        this._confirmedStep.set(current.pointer.step);
        this._targetStep.set(current.pointer.step);
        this._currentPayload.set(current.payload);
        this._mode.set('story-demo');
      })
      .catch((err) => {
        this._isAvailable.set(false);
        this._mode.set('default-template');
        this.showServiceError(err);
      })
      .finally(() => this._isLoading.set(false));
  }

  // ---------------------------------------------------------------------------
  // Private — startup orchestration
  // ---------------------------------------------------------------------------

  /**
   * Startup flow (non-blocking):
   * 1. Probe GET /api/story/demos.
   * 2. On success: populate dropdown; if saved ID is in list → load it; if not → clear + toast.
   * 3. On network failure: if saved ID exists → attempt getCurrent directly; else toast unavailable.
   */
  private initializeFromDemos(): void {
    const savedId = this._demoId();
    this.story
      .getDemos()
      .then((res) => {
        if (!res || !Array.isArray(res.demos)) {
          throw new Error('Invalid Story Service demos response.');
        }
        this._isAvailable.set(true);
        this._availableDemos.set(res.demos);
        if (!savedId) {
          // No saved ID — populate dropdown, stay in default-template.
          return;
        }
        const inList = res.demos.some((d) => d.demoId === savedId);
        if (inList) {
          this.loadDemoAndEnterStoryMode(savedId);
        } else {
          // Saved ID is stale — clear it and notify.
          this.clearSavedDemoId();
          this.showToast(
            'warn',
            `Saved demo "${savedId}" is no longer available. Using Default Template.`
          );
        }
      })
      .catch((err) => {
        this._isAvailable.set(false);
        this._availableDemos.set([]);
        // /demos failed — service may be unreachable.
        if (savedId) {
          // Attempt direct /current for saved ID as a fallback.
          this.story
            .getCurrent(savedId)
            .then((current) => {
              // Service is reachable for this demo even though /demos failed.
              this._currentStep.set(current.pointer.step);
              this._confirmedStep.set(current.pointer.step);
              this._targetStep.set(current.pointer.step);
              this._currentPayload.set(current.payload);
              this._isAvailable.set(true);
              this._mode.set('story-demo');
              // Fetch meta separately to get maxStep / recommendedStartStep.
              return this.story.getMeta(savedId);
            })
            .then((meta) => {
              this._maxStep.set(meta.maxStep);
              this._recommendedStartStep.set(meta.recommendedStartStep);
            })
            .catch(() => {
              // Direct /current also failed — clear saved ID, stay default-template.
              this.clearSavedDemoId();
              this._isAvailable.set(false);
              // Silent startup fallback: settings panel communicates unavailable state.
            });
        } else {
          // No saved ID and /demos failed — stay on default-template silently.
          // Settings panel communicates unavailable state without a disruptive toast.
        }
      });
  }

  /**
   * Load a demo by ID: fetch meta + current step, then enter story-demo mode.
   * Called both on startup (validated ID) and on explicit user selection.
   */
  private loadDemoAndEnterStoryMode(id: string): void {
    this._isLoading.set(true);
    this.story
      .getMeta(id)
      .then((meta) => {
        this._maxStep.set(meta.maxStep);
        this._recommendedStartStep.set(meta.recommendedStartStep);
        return this.story.getCurrent(id);
      })
      .then((current) => {
        console.log('[StoryDemoFacade] loadDemoAndEnterStoryMode success', {
          step: current.pointer.step,
          payloadKeys: Object.keys(current.payload),
        });
        this._currentStep.set(current.pointer.step);
        this._confirmedStep.set(current.pointer.step);
        this._targetStep.set(current.pointer.step);
        this._currentPayload.set(current.payload);
        this._isAvailable.set(true);
        this._mode.set('story-demo');
      })
      .catch((err) => {
        console.error('[StoryDemoFacade] loadDemoAndEnterStoryMode error', err);
        this._isAvailable.set(false);
        this._mode.set('default-template');
        this.showServiceError(err);
      })
      .finally(() => this._isLoading.set(false));
  }

  private clearSavedDemoId(): void {
    this.clearStepSyncTimer();
    this._demoId.set('');
    this._mode.set('default-template');
    this._currentPayload.set({});
    this._currentStep.set(1);
    this._confirmedStep.set(1);
    this._targetStep.set(1);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORY_DEMO_ID_STORAGE_KEY);
    }
  }

  private loadDemoIdFromStorage(): void {
    if (typeof localStorage === 'undefined') return;
    const stored = localStorage.getItem(STORY_DEMO_ID_STORAGE_KEY);
    if (stored?.trim()) {
      this._demoId.set(stored.trim());
    }
  }

  // ---------------------------------------------------------------------------
  // Private — optimistic step target + queued sync to Story Service
  // ---------------------------------------------------------------------------

  private enqueueTargetStep(step: number): void {
    this._currentStep.set(step); // optimistic UI update
    this._targetStep.set(step);
    this.scheduleSyncToTarget();
  }

  private scheduleSyncToTarget(): void {
    this.clearStepSyncTimer();
    this._stepSyncTimer = setTimeout(() => {
      this._stepSyncTimer = null;
      this.syncToTarget();
    }, StoryDemoFacade.STEP_SYNC_DEBOUNCE_MS);
  }

  private clearStepSyncTimer(): void {
    if (this._stepSyncTimer) {
      clearTimeout(this._stepSyncTimer);
      this._stepSyncTimer = null;
    }
  }

  private syncToTarget(): void {
    if (this._syncInFlight) return;
    this.clearStepSyncTimer();
    const demoId = this._demoId();
    if (!demoId || !this._isAvailable() || this._mode() !== 'story-demo') return;

    this._syncInFlight = true;
    this._isLoading.set(true);

    const loop = (): Promise<void> => {
      const target = this._targetStep();
      const confirmed = this._confirmedStep();
      if (!this._forceRefresh && target === confirmed) {
        return Promise.resolve();
      }

      return this.story
        .jump(demoId, target)
        .then(() => this.story.getCurrent(demoId))
        .then((current) => {
          // If the demo changed mid-flight, ignore this response.
          if (this._demoId() !== demoId || this._mode() !== 'story-demo') return;

          const resolvedStep = current.pointer.step;
          const latestTarget = this._targetStep();
          this._confirmedStep.set(resolvedStep);
          this._isAvailable.set(true);

          // Safety guard: if backend did not move to requested target and no
          // newer target superseded it, stop retrying to avoid an infinite loop.
          if (resolvedStep !== target && latestTarget === target) {
            throw new Error(
              `Unable to move pointer to requested step ${target}.`
            );
          }

          // Only apply payload when response matches the latest requested step.
          if (resolvedStep === latestTarget) {
            this._currentStep.set(resolvedStep);
            this._currentPayload.set(current.payload);
            this._forceRefresh = false;
          }
        })
        .then(() => {
          if (this._demoId() !== demoId || this._mode() !== 'story-demo') {
            return Promise.resolve();
          }
          if (this._forceRefresh || this._targetStep() !== this._confirmedStep()) {
            return loop();
          }
          return Promise.resolve();
        });
    };

    loop()
      .catch((err) => {
        // Revert optimistic UI to last confirmed step on error.
        this._targetStep.set(this._confirmedStep());
        this._currentStep.set(this._confirmedStep());
        this._forceRefresh = false;
        this.showServiceError(err);
      })
      .finally(() => {
        this._syncInFlight = false;
        this._isLoading.set(false);
        // Pick up any target changes that happened after final await.
        if (
          this._mode() === 'story-demo' &&
          this._isAvailable() &&
          this._demoId() === demoId &&
          (this._forceRefresh || this._targetStep() !== this._confirmedStep())
        ) {
          this.syncToTarget();
        }
      });
  }

  // ---------------------------------------------------------------------------
  // Private — toast helpers
  // ---------------------------------------------------------------------------

  private showToast(severity: string, detail: string): void {
    this.messageService.add({
      severity,
      summary: 'Demo',
      detail,
      life: 4000,
    });
  }

  private showServiceError(
    err: Error & { status?: number; code?: string }
  ): void {
    this._isAvailable.set(false);
    const status = err.status;
    const code = err.code;
    const msg = err.message;
    if (status === 401 || code === 'UNAUTHORIZED') {
      this.messageService.add({
        severity: 'error',
        summary: 'Story Service',
        detail: 'Invalid or missing demo key.',
        life: 6000,
      });
      return;
    }
    if (status === 404 || code === 'NOT_FOUND') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Story Service',
        detail: 'Demo or step not found.',
        life: 5000,
      });
      return;
    }
    if (status === 400 || code === 'BAD_REQUEST') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Story Service',
        detail: msg || 'Invalid request.',
        life: 5000,
      });
      return;
    }
    if (status === 409 || code === 'CONFLICT') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Story Service',
        detail: msg || 'Conflict.',
        life: 5000,
      });
      return;
    }
    const unreachableDetail =
      status === 0 || status == null
        ? 'StoryService unavailable. Unable to connect to backend.'
        : msg || 'Story Service is unavailable.';
    this.messageService.add({
      severity: 'error',
      summary: 'Story Service',
      detail: unreachableDetail,
      life: 6000,
    });
  }
}
